import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable, Subscription } from 'rxjs';
import { LossReasonDto } from 'src/app/shared/dto/loss-reason.dto';
import { LossReasonsResponseDto } from 'src/app/shared/dto/pagination/loss-reasons-response.dto';
import { BaseService } from 'src/app/shared/services/base.service';
import { SocketService } from 'src/app/shared/services/socket.service';
import { Messages } from 'src/app/shared/sockets/enums/messages';
import { formatClientRoom, Client, RoomNames, formatClientRoomId } from 'src/app/shared/sockets/enums/rooms';

@Injectable({
    providedIn: 'root',
})
export class LossReasonsService extends BaseService {
    public lossReasons$: Observable<LossReasonDto[]>;
    public lossReasonById$: Observable<LossReasonDto | null>;
    public lossReasonsPaginated$: Observable<LossReasonsResponse>;

    private lossReasonsSubject = new BehaviorSubject<LossReasonDto[]>([]);
    private messageSubscription: Subscription | undefined;

    private lossReasonsPaginatedSubject = new BehaviorSubject<LossReasonsResponse>(new LossReasonsResponse);
    private messagePaginatedSubscription: Subscription | undefined;

    private lossReasonSubjectById = new BehaviorSubject<LossReasonDto | null>(null);
    private messageSubscriptionById: Subscription | undefined;

    private pageNumber: number = 1;
    private pageSize: number = 20;
    private filterText: string = '';

    private readonly socketService = inject(SocketService);

    constructor(httpClient: HttpClient) {
        super(httpClient, 'loss-reasons');

        this.lossReasons$ = this.lossReasonsSubject.asObservable();
        this.lossReasonById$ = this.lossReasonSubjectById.asObservable();
        this.lossReasonsPaginated$ = this.lossReasonsPaginatedSubject.asObservable();
    }

    refreshVariables(pageSize: number, pageNumber: number, filterText: string): void {
        this.pageSize = pageSize;
        this.pageNumber = pageNumber;
        this.filterText = filterText;
    }

    async setupWebSocket(): Promise<void> {
        try {
            const lossReasons = await this.get();
            this.lossReasonsSubject.next(lossReasons);

            this.socketService.conectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.LOSS_REASONS_ROOM));
            this.messageSubscription = this.socketService.getNotifications(Messages.LOSS_REASONS_CHANGED).subscribe(async () => {
                const lossReasons = await this.get();
                this.lossReasonsSubject.next(lossReasons);
            });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de Razones de mermas. Error: ${e.message}`);
        }
    }

    disconnectWebSocket(): void {
        if(this.messageSubscription){
            this.messageSubscription.unsubscribe();
        }
        this.socketService.disconnectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.LOSS_REASONS_ROOM));
    }

    async setupWebSocketPaginated(pageSize: number, pageNumber: number): Promise<void> {
        this.pageSize = pageSize;
        this.pageNumber = pageNumber;

        try {
            const response = await this.getPaginatedLossReasons(this.pageSize, this.pageNumber, this.filterText);
            this.lossReasonsPaginatedSubject.next({ lossReasons: response.lossReasons, totalLossReasons: response.total });

            this.socketService.conectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.LOSS_REASONS_ROOM));
            this.messagePaginatedSubscription = this.socketService.getNotifications(Messages.LOSS_REASONS_CHANGED).subscribe(async () => {
                const response = await this.getPaginatedLossReasons(this.pageSize, this.pageNumber, this.filterText);
                this.lossReasonsPaginatedSubject.next({ lossReasons: response.lossReasons, totalLossReasons: response.total });
            });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de Razones de mermas paginadas. Error: ${e.message}`);
        }
    }

    disconnectWebSocketPaginated(): void {
        this.messagePaginatedSubscription!.unsubscribe();
        this.socketService.disconnectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.LOSS_REASONS_ROOM));
    }

    async setupWebSocketById(lossReasonId: number): Promise<void> {
        try {
            this.socketService.conectSocketRoom(formatClientRoomId(Client.DEFAULT_CLIENT, RoomNames.LOSS_REASONS_ROOM, lossReasonId));
            this.messageSubscriptionById = this.socketService.getNotifications(Messages.LOSS_REASON_CHANGED).subscribe(async (lossReason : LossReasonDto) => {
                this.lossReasonSubjectById.next(lossReason);
            });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de Razón de merma con ID ${lossReasonId}. Error: ${e.message}`);
        }
    }

    disconnectWebSocketById(lossReasonId: number): void {
        this.messageSubscriptionById!.unsubscribe();
        this.lossReasonSubjectById.next(null);
        this.socketService.disconnectSocketRoom(formatClientRoomId(Client.DEFAULT_CLIENT, RoomNames.LOSS_REASONS_ROOM, lossReasonId));
    }

    async getPaginatedLossReasons(pageSize: number, pageNumber: number, filterText: string): Promise<LossReasonsResponseDto> {
        try {
            let url: string = `${this.url}/findAll?pageSize=${pageSize}&pageNumber=${pageNumber}`;

            if (filterText && filterText != '') {
                url = `${url}&filterText=${filterText}`;
            }

            const request = this.httpClient.get<LossReasonsResponseDto>(url);

            const lossReasonsResponse: LossReasonsResponseDto = await lastValueFrom(request);

            return lossReasonsResponse;
        } catch (e: any) {
            throw new Error(`No se ha encontrado motivos de merma. Error: ${e.message}`);
        }
    }

}

export class LossReasonsResponse {
    lossReasons!: LossReasonDto[];
    totalLossReasons!: number;
}
